import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowLeft, ArrowRight, BookOpen, Users, CreditCard, GraduationCap, Briefcase, Mic, Dumbbell, Palette, Code, Calculator, Gift, ShieldCheck, Clock, Languages } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useEffect, useState, lazy, Suspense } from "react";
// Heavy hero components (desktop-only dashboard mock, decorative SVG sparkles)
// are lazy-loaded so mobile visitors don't pay their parse/render cost.
const LandingHeroPreview = lazy(() => import("./LandingHeroPreview"));
const HeroSparkles = lazy(() => import("./landing/HeroSparkles"));
import { analytics } from "@/lib/analytics";

function AnimatedCounter({ end, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    // Use requestAnimationFrame instead of setInterval to reduce
    // main-thread work, avoid forced reflow, and stay in sync with paint.
    let rafId;
    const startTime = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      // ease-out for a calmer landing on final value
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(end * eased));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [end, duration]);
  return <>{count.toLocaleString("en-US")}{suffix}</>;
}

export default function LandingHero() {
  const { t, lang, isRTL } = useI18n();
  const isAr = lang === "ar";
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  // Defer decorative sparkles + dashboard preview until after first paint /
  // when matchMedia(>= lg) is true. Prevents loading 300+ LOC of preview
  // markup on mobile (where the desktop column is `hidden`) and avoids
  // animated sparkle layout shift on small viewports during initial paint.
  const [decorReady, setDecorReady] = useState(false);
  const [isLg, setIsLg] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const apply = () => setIsLg(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    const idle = (cb) =>
      'requestIdleCallback' in window
        ? window.requestIdleCallback(cb, { timeout: 1500 })
        : setTimeout(cb, 600);
    const handle = idle(() => setDecorReady(true));
    return () => {
      mq.removeEventListener?.('change', apply);
      if ('cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);

  const stats = [
    { icon: BookOpen, value: 500, suffix: "+", label: isAr ? "دورة منشورة" : "Courses Published" },
    { icon: Users, value: 10000, suffix: "+", label: isAr ? "طالب مسجّل" : "Students Enrolled" },
    { icon: CreditCard, value: 98, suffix: "%", label: isAr ? "رضا المدربين" : "Instructor Satisfaction" },
  ];

  return (
    <section id="hero" className="relative overflow-hidden scroll-mt-20">
      {/*
        Phase 2 background optimization:
        - Mobile (default): only the static dot grid renders. No animated
          blurred blobs, no negative-offset absolute elements that caused
          layout/paint cost during initial scroll on mobile.
        - Desktop (lg+): full animated blob composition is restored.
        - All animations are transform/opacity only via existing
          `animate-fog-*` keyframes (defined in Tailwind config).
        - Containing wrapper is `overflow-hidden` + `inset-0` to guarantee
          blobs never extend the document box (prevents horizontal scroll
          and avoids contributing to CLS).
      */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none motion-reduce:hidden lg:block hidden">
        <div
          className="absolute top-[-10%] start-[10%] w-[600px] h-[600px] rounded-full blur-[120px] animate-fog-drift opacity-[0.10]"
          style={{ background: 'radial-gradient(circle, hsl(var(--brand)) 0%, transparent 70%)', willChange: 'transform' }}
        />
        <div
          className="absolute top-[5%] end-[5%] w-[500px] h-[500px] rounded-full blur-[100px] animate-fog-drift-reverse opacity-[0.08]"
          style={{ background: 'radial-gradient(circle, hsl(var(--brand-secondary)) 0%, transparent 70%)', willChange: 'transform' }}
        />
        <div
          className="absolute top-[15%] start-[30%] w-[400px] h-[400px] rounded-full blur-[80px] animate-fog-breathe opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, hsl(var(--brand)) 20%, hsl(var(--brand-secondary)) 60%, transparent 80%)', willChange: 'transform' }}
        />
      </div>
      {/* Static dot grid — cheap, no animation, safe on mobile and reduced-motion. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.02] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(hsl(var(--brand)) 1px, transparent 1px)', backgroundSize: '32px 32px' }}
      />
      {decorReady && (
        <Suspense fallback={null}>
          <HeroSparkles />
        </Suspense>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8 xl:gap-12 lg:items-center">
          <div className="max-w-4xl mx-auto text-center lg:text-start lg:col-span-6 xl:col-span-7 lg:mx-0">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 mt-2">
            {isAr ? (
              <>خبرتك تستاهل<span className="text-brand"> أكاديمية </span>تليق فيها</>
            ) : (
              <>Turn your expertise into a<span className="text-brand"> digital academy </span>that sells itself</>
            )}
          </h1>

          <p className="text-base sm:text-lg text-brand font-semibold mb-4">
            {isAr
              ? "حوّل علمك إلى مصدر دخل ثابت، وانفع غيرك وأنت مرتاح."
              : "Turn your knowledge into income, and help others along the way."}
          </p>

          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            {isAr
              ? <>مع <strong className="text-foreground">دورة</strong> تبني <strong>أكاديميتك التعليمية</strong> الخاصة فيك، ترفع دوراتك، وتستقبل مدفوعات طلابك. كل شي جاهز لك بدون ما تحتاج أي خبرة تقنية.</>
              : <><strong className="text-foreground">Dwrrah</strong> helps you turn your <strong>expertise</strong> into a complete <strong>digital academy</strong>. Build your site, upload your courses, run AI-powered quizzes, and collect payments without any technical skills.</>
            }
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="bg-gradient-to-r from-brand to-brand-secondary hover:opacity-90 text-brand-foreground text-base px-8 gap-2 h-12 shadow-lg shadow-brand/20 rounded-xl press-scale" asChild>
              <Link to="/signup" onClick={() => analytics.signupCtaClick({ cta_type: "hero_start_free", source_page: "/" })}>
                {isAr ? "أنشئ أكاديميتك الآن مجاناً" : "Build Your Academy Free"}
                <Arrow className="w-4 h-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="text-base px-8 gap-2 h-12 rounded-xl press-scale" asChild>
              <a
                href="#pricing"
                aria-label={isAr ? "اعرض خطط الأسعار في صفحة دورة الرئيسية" : "View Dwrrah pricing plans on the home page"}
              >
                {isAr ? "شوف الأسعار" : "View Pricing"}
              </a>
            </Button>
          </div>

          {(() => {
            const trustItems = isAr ? [
              { icon: Gift, label: "تجربة مجانية 14 يوم", strong: true },
              { icon: ShieldCheck, label: "بدون بطاقة ائتمان", strong: true },
              { icon: Clock, label: "تجهز في 5 دقائق" },
              { icon: Languages, label: "دعم عربي كامل" },
            ] : [
              { icon: Gift, label: "14-day free trial", strong: true },
              { icon: ShieldCheck, label: "No credit card", strong: true },
              { icon: Clock, label: "Setup in 5 minutes" },
              { icon: Languages, label: "Full Arabic support" },
            ];
            return (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px]">
                {trustItems.map((item, i) => (
                  <span key={i} className="inline-flex items-center gap-2">
                    {i > 0 && <span className="hidden sm:inline-block w-px h-3.5 bg-border -ms-3" aria-hidden="true" />}
                    <item.icon className="w-3.5 h-3.5 text-brand" strokeWidth={2.25} />
                    <span className="font-bold text-foreground">
                      {item.label}
                    </span>
                  </span>
                ))}
              </div>
            );
          })()}

          {/*
            Mobile/tablet inline preview removed from initial render.
            The desktop-style dashboard mock is heavy and adds ~300 LOC of
            markup that contributed to mobile TBT/CLS. The same component
            still renders on lg+ in the column below. Mobile visitors now
            see the cleaner CTA stack -> trust badges -> marquee flow.
          */}

          <p className="font-medium mt-10 mb-1 text-xl text-brand">
            {isAr ? "مصممة للمستقلين والأفراد اللي يبون يبنون شي خاص فيهم" : "Built for independent creators who want their own platform"}
          </p>

          {/* Target audience marquee */}
          {(() => {
            const items = isAr ? [
              { icon: GraduationCap, label: "مدرس خصوصي" },
              { icon: Briefcase, label: "مدرب مهني" },
              { icon: Mic, label: "متحدث ومحاضر" },
              { icon: Dumbbell, label: "مدرب لياقة" },
              { icon: Palette, label: "مصمم ومبدع" },
              { icon: Code, label: "مبرمج مستقل" },
              { icon: Calculator, label: "محاسب ومستشار" },
            ] : [
              { icon: GraduationCap, label: "Private Tutor" },
              { icon: Briefcase, label: "Career Coach" },
              { icon: Mic, label: "Speaker" },
              { icon: Dumbbell, label: "Fitness Trainer" },
              { icon: Palette, label: "Designer" },
              { icon: Code, label: "Freelance Dev" },
              { icon: Calculator, label: "Consultant" },
            ];
            const renderSet = (key) => items.map((item, i) => (
              <span key={`${key}-${i}`} className="inline-flex items-center gap-2 px-5 py-2 bg-brand/10 rounded-full text-[13px] font-medium text-brand shrink-0">
                <item.icon className="w-4 h-4" />
                {item.label}
              </span>
            ));
            return (
              <div className="mt-6 relative overflow-hidden py-3">
                <div className="absolute inset-y-0 right-0 w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, transparent, hsl(var(--background)))' }} />
                <div className="absolute inset-y-0 left-0 w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, transparent, hsl(var(--background)))' }} />
                <div className="marquee-track gap-4 whitespace-nowrap">
                  {renderSet("a")}
                  <span className="shrink-0 w-4" aria-hidden="true" />
                  {renderSet("b")}
                  <span className="shrink-0 w-4" aria-hidden="true" />
                </div>
              </div>
            );
          })()}
        </div>
          <div className="hidden lg:block lg:col-span-6 xl:col-span-5 min-h-[520px]">
            {isLg && (
              <Suspense fallback={<div className="w-full h-[520px]" aria-hidden="true" />}>
                <LandingHeroPreview />
              </Suspense>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-14 grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col items-center justify-center text-center p-4 sm:p-5 bg-card/60 backdrop-blur-sm rounded-2xl border border-border/40 hover:border-brand/20 hover-lift transition-all duration-300 min-h-[130px]">
              <s.icon className="w-5 h-5 text-brand mb-2.5" />
              <div className="text-2xl sm:text-3xl font-bold text-foreground leading-none">
                <AnimatedCounter end={s.value} suffix={s.suffix} />
              </div>
              <div className="text-xs sm:text-[13px] text-muted-foreground mt-2 leading-tight">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
